import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AlignJustify, Code2, LayoutGrid, Lock, Plus } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import type { DateRange } from 'react-day-picker';
import { DateRangeFilter } from 'components/blocks/data-table/data-table-date-filter';
import { DataTableColumnHeader } from 'components/blocks/data-table/data-table-column-header';
import DataTable from 'components/blocks/data-table/data-table';
import { CustomtDateFormat } from 'lib/custom-date-formatter';
import { Search, StatsReport } from 'iconoir-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { useTheme } from 'components/core/theme-provider';
import { cn } from 'lib/utils';
import { useConfig } from 'hooks/use-config';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useAssistantFileContent } from 'features/chat/hooks/use-assistant-file-content';
import { MessageRole } from 'features/chat/types/chat';
import {
  useDashboardTitle,
  useUntitledDashboardLabel,
} from 'features/chat/hooks/use-dashboard-title';
import { isDashboardAttachment } from 'features/chat/utils/assistant-file-preview';
import { dashboardDocument } from 'features/chat/utils/dashboard-document';
import { useDashboardTitleStore } from 'state/store/chat/use-dashboard-title-store';
import { formatFileSize } from 'features/file-manager/utils/file-manager';
import {
  DASHBOARDS_PAGE_SIZE,
  fetchDashboards,
  type DashboardFile,
} from 'pages/dashboard/dashboard-service';

type DashboardTab = 'all' | 'yours' | 'shared';
type DashboardView = 'grid' | 'list';
type Translate = (key: string, options?: Record<string, unknown> | string) => string;

type TitleOf = (dashboard: DashboardFile) => string;

/**
 * The title stored with the latest version, else the one read from the file once its
 * thumbnail loaded, then the conversation title; never the stored `.dashboard` file name.
 * A card stands for the whole dashboard, so it carries no version number.
 */
const useDashboardCardTitle = (): TitleOf => {
  const titles = useDashboardTitleStore((state) => state.titles);
  const untitled = useUntitledDashboardLabel();
  return (dashboard) =>
    dashboard.title?.trim() ||
    titles[dashboard.fileId] ||
    dashboard.conversationTitle ||
    (isDashboardAttachment(dashboard.fileName, dashboard.extension)
      ? untitled
      : dashboard.fileName);
};

const dashboardColumns = (
  t: Translate,
  titleColor: string,
  dashboardTitle: TitleOf
): ColumnDef<DashboardFile>[] => [
  {
    id: 'name',
    accessorFn: (row) => dashboardTitle(row),
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('NAME', { defaultValue: 'Name' })} />
    ),
    cell: ({ row }) => (
      <div className={cn('flex min-w-0 items-center gap-2', titleColor)}>
        <Code2 className="h-4 w-4 shrink-0" />
        <span className="min-w-0 truncate">{dashboardTitle(row.original)}</span>
      </div>
    ),
  },
  {
    id: 'createdDate',
    accessorFn: (row) => row.createdDate,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title={t('CREATED_DATE', { defaultValue: 'Created Date' })}
      />
    ),
    cell: ({ row }) => CustomtDateFormat(row.original.createdDate, { dateDelimiter: '.' }),
  },
  {
    id: 'type',
    accessorFn: () => 'dashboard',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TYPE', { defaultValue: 'Type' })} />
    ),
    cell: () => t('DASHBOARD', { defaultValue: 'Dashboard' }),
  },
  {
    id: 'size',
    accessorFn: (row) => row.size,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('SIZE', { defaultValue: 'Size' })} />
    ),
    cell: ({ row }) => (row.original.size > 0 ? formatFileSize(row.original.size) : '-'),
  },
];

function DashboardThumbnail({
  dashboard,
  title,
  mutedColor,
}: Readonly<{ dashboard: DashboardFile; title: string; mutedColor: string }>) {
  const { t } = useTranslation();
  const attachment = useMemo(() => ({ ...dashboard, sender: MessageRole.ASSISTANT }), [dashboard]);
  const { content, isLoading, error } = useAssistantFileContent(attachment);
  // Shares the title read from the content with the card heading and list row.
  useDashboardTitle(dashboard, content);
  const srcDoc = useMemo(
    () => (content ? dashboardDocument(content, { preview: true }) : ''),
    [content]
  );

  if (!content) {
    let placeholder = t('NO_PREVIEW', { defaultValue: 'No preview' });
    if (error) placeholder = t('PREVIEW_UNAVAILABLE', { defaultValue: 'Preview unavailable' });
    else if (isLoading) placeholder = t('LOADING_PREVIEW', { defaultValue: 'Loading preview…' });
    return (
      <div className={cn('flex h-full w-full items-center justify-center text-sm', mutedColor)}>
        {placeholder}
      </div>
    );
  }

  return (
    // Render at twice the card size and scale down, so the thumbnail shows the whole
    // dashboard rather than only its header.
    <iframe
      title={title}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      srcDoc={srcDoc}
      tabIndex={-1}
      className="pointer-events-none absolute left-0 top-0 h-[200%] w-[200%] origin-top-left scale-50 border-0 bg-true-white"
    />
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { configurationSettings } = useConfig();
  const dashboardTitle = useDashboardCardTitle();
  const setNewChat = useChatStore((state) => state.setNewChat);
  const setIsIncognitoChat = useChatStore((state) => state.setIsIncognitoChat);
  const userId = useCommonStore((state) => state.user?.id);
  const [tab, setTab] = useState<DashboardTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [createdDate, setCreatedDate] = useState<DateRange | undefined>();
  const [viewMode, setViewMode] = useState<DashboardView>('grid');
  const [page, setPage] = useState(1);
  const titleColor = theme === 'dark' ? 'text-true-white' : 'text-grey-900';
  const mutedColor = theme === 'dark' ? 'text-grey-300' : 'text-grey-500';
  const search = searchQuery.trim();
  // Same epoch-millisecond bounds as the media-library file filters.
  const startTs = createdDate?.from?.getTime();
  const endTs = createdDate?.to?.getTime();

  const dashboardsQuery = useQuery({
    queryKey: [
      'dashboard-artifacts',
      configurationSettings?.blocksKey,
      page,
      search,
      startTs,
      endTs,
    ],
    queryFn: () => fetchDashboards(configurationSettings, page, search, { startTs, endTs }),
    enabled: Boolean(configurationSettings?.paths?.seliseAiBaseUrl),
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
  const totalCount = dashboardsQuery.data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / DASHBOARDS_PAGE_SIZE));
  const dashboards = (dashboardsQuery.data?.data ?? []).filter((dashboard) => {
    const isYours = !dashboard.ownerId || dashboard.ownerId === userId;
    if (tab === 'yours') return isYours;
    if (tab === 'shared') return !isYours;
    return true;
  });

  const openNewDashboardChat = () => {
    setNewChat(true);
    setIsIncognitoChat(false);
    // Keep the user's selected agent; the regular composer owns selection.
    navigate('/chat?dashboard=new');
  };

  const openDashboard = (dashboard: DashboardFile) => {
    // The saved conversation decides dashboard mode; the state only avoids a
    // normal-chat flash until it loads.
    navigate(`/chat/${encodeURIComponent(dashboard.conversationId)}`, {
      state: { dashboard: true },
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleCreatedDateChange = (range: DateRange | undefined) => {
    setCreatedDate(range);
    setPage(1);
  };

  const yoursCount = (dashboardsQuery.data?.data ?? []).filter(
    (dashboard) => !dashboard.ownerId || dashboard.ownerId === userId
  ).length;
  const tabs: { id: DashboardTab; label: string; count: number }[] = [
    { id: 'all', label: t('ALL', { defaultValue: 'All' }), count: totalCount },
    { id: 'yours', label: t('YOURS', { defaultValue: 'Yours' }), count: yoursCount },
  ];

  const renderDashboards = () => {
    if (dashboardsQuery.isError) {
      return (
        <div role="alert" className="flex flex-col items-start gap-3">
          <p className={cn('text-sm leading-body', mutedColor)}>
            {t('DASHBOARDS_LOAD_FAILED', {
              defaultValue: 'Could not load dashboards. Please try again.',
            })}
          </p>
          <Button
            type="button"
            variant="outline"
            className="h-10 rounded-full px-4"
            disabled={dashboardsQuery.isFetching}
            onClick={() => void dashboardsQuery.refetch()}
          >
            {t('RETRY', { defaultValue: 'Retry' })}
          </Button>
        </div>
      );
    }

    if (dashboardsQuery.isPending) {
      return (
        <p role="status" className={cn('text-sm leading-body', mutedColor)}>
          {t('LOADING_DASHBOARDS', { defaultValue: 'Loading dashboards…' })}
        </p>
      );
    }

    if (viewMode === 'list') {
      return (
        <div data-testid="dashboard-list" className="min-w-0">
          <DataTable
            columns={dashboardColumns(t, titleColor, dashboardTitle)}
            data={dashboards}
            onRowClick={openDashboard}
            isLoading={dashboardsQuery.isFetching}
            pagination={{ pageIndex: page - 1, pageSize: DASHBOARDS_PAGE_SIZE, totalCount }}
            onPaginationChange={({ pageIndex }) => setPage(pageIndex + 1)}
            manualPagination
            mobileColumns={['name']}
            mobileProperties={['createdDate', 'type', 'size']}
          />
        </div>
      );
    }

    return (
      <>
        {dashboards.length === 0 ? (
          <p className={cn('text-sm leading-body', mutedColor)}>
            {t('NO_DASHBOARDS', { defaultValue: 'No dashboards' })}
          </p>
        ) : (
          <ul data-testid="dashboard-grid" className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {dashboards.map((dashboard) => (
              <li key={dashboard.conversationId}>
                <article className="relative overflow-hidden rounded-card border border-grey-100 bg-card dark:border-border">
                  <button
                    type="button"
                    aria-label={t('OPEN_DASHBOARD', {
                      defaultValue: 'Open {{title}}',
                      title: dashboardTitle(dashboard),
                    })}
                    onClick={() => openDashboard(dashboard)}
                    className="absolute inset-0 z-10"
                  />
                  <div className="relative h-48 overflow-hidden bg-grey-50 dark:bg-true-white/10">
                    <DashboardThumbnail
                      dashboard={dashboard}
                      title={dashboardTitle(dashboard)}
                      mutedColor={mutedColor}
                    />
                    <Code2 className={cn('absolute bottom-3 left-3 h-4 w-4', mutedColor)} />
                  </div>
                  <div className="flex flex-col gap-1 p-4">
                    <h3 className={cn('truncate text-base font-medium', titleColor)}>
                      {dashboardTitle(dashboard)}
                    </h3>
                    <p className={cn('flex items-center gap-1 text-xs', mutedColor)}>
                      <Lock className="h-3 w-3" />
                      {t('DASHBOARD_EDITED_ON', {
                        defaultValue: 'Edited {{date}}',
                        date: CustomtDateFormat(dashboard.createdDate, { dateDelimiter: '.' }),
                      })}
                    </p>
                  </div>
                </article>
              </li>
            ))}
          </ul>
        )}
        {totalCount > DASHBOARDS_PAGE_SIZE && (
          <div className="flex items-center justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              className="h-10 rounded-full px-4"
              disabled={page <= 1 || dashboardsQuery.isFetching}
              onClick={() => setPage(page - 1)}
            >
              {t('PREVIOUS', { defaultValue: 'Previous' })}
            </Button>
            <span className={cn('text-sm', mutedColor)}>
              {t('PAGE_OF', {
                defaultValue: 'Page {{page}} of {{total}}',
                page,
                total: totalPages,
              })}
            </span>
            <Button
              type="button"
              variant="outline"
              className="h-10 rounded-full px-4"
              disabled={page >= totalPages || dashboardsQuery.isFetching}
              onClick={() => setPage(page + 1)}
            >
              {t('NEXT', { defaultValue: 'Next' })}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="h-full min-h-0 w-full overflow-y-auto overflow-x-hidden">
      <div className="mx-auto flex w-full max-w-wide flex-col gap-6 px-4 py-4 sm:px-6 sm:py-6">
        <div className="flex flex-col gap-4 border-b border-grey-100 pb-4 dark:border-border sm:gap-6 sm:pb-6">
          <header className="flex w-full flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 flex-col gap-2">
              <div className="flex h-8 min-w-0 items-center gap-2">
                <div className="flex size-8 shrink-0 items-center justify-center">
                  <StatsReport width={20} height={20} strokeWidth={1.5} className={titleColor} />
                </div>
                <h1
                  className={cn(
                    'min-w-0 truncate text-2xl font-medium leading-heading',
                    titleColor
                  )}
                >
                  {t('DASHBOARD', { defaultValue: 'Dashboard' })}
                </h1>
              </div>
              <p
                className={cn('min-w-0 text-sm font-normal leading-body sm:text-base', titleColor)}
              >
                {t('DASHBOARD_PAGE_DESCRIPTION', {
                  defaultValue:
                    'Collections of dashboards that you have used in previous conversations',
                })}
              </p>
            </div>
            <Button
              type="button"
              className="h-11 w-full shrink-0 gap-2 px-4 text-base font-medium shadow-none sm:w-auto"
              onClick={openNewDashboardChat}
            >
              <Plus className="h-4 w-4" />
              {t('ADD_DASHBOARD', { defaultValue: 'Add dashboard' })}
            </Button>
          </header>
          <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 dark:border-border dark:bg-card sm:max-w-search">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center">
              <Search width={15} height={15} strokeWidth={1.75} className="text-grey-400" />
            </div>
            <Input
              value={searchQuery}
              onChange={(event) => handleSearchChange(event.target.value)}
              placeholder={t('SEARCH_DASHBOARDS_BY_NAME', { defaultValue: 'Search by name' })}
              aria-label={t('SEARCH_DASHBOARDS', { defaultValue: 'Search dashboards' })}
              className={cn(
                'h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-base font-normal leading-heading shadow-none placeholder:text-grey-400 focus-visible:ring-0 md:text-sm dark:bg-transparent',
                titleColor
              )}
            />
          </div>
        </div>
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex max-w-full min-w-0 shrink-0 flex-nowrap items-center gap-2 overflow-x-auto">
            {tabs.map((item) => {
              const isActive = tab === item.id;
              let countColor = 'text-grey-500';
              if (theme === 'dark' && isActive) countColor = 'text-true-white/80';
              else if (theme === 'dark') countColor = 'text-true-white/55';

              return (
                <button
                  key={item.id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setTab(item.id)}
                  className={cn(
                    'inline-flex h-10 shrink-0 items-center rounded-lg border px-3 text-sm font-normal leading-heading transition-colors',
                    isActive
                      ? cn(
                          'border-grey-100 bg-grey-50 dark:border-true-white/40 dark:bg-true-white/15',
                          titleColor
                        )
                      : cn(
                          'border-transparent hover:bg-grey-50 dark:hover:bg-true-white/10',
                          theme === 'dark'
                            ? 'text-true-white/75 hover:text-true-white'
                            : 'text-grey-500 hover:text-grey-900'
                        )
                  )}
                >
                  {item.label}
                  <span aria-hidden="true" className={cn('ml-1.5 text-xs', countColor)}>
                    {item.count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <DateRangeFilter
              title="CREATED_DATE"
              date={createdDate}
              onDateChange={handleCreatedDateChange}
              iconPosition="end"
              forceOutline
              triggerClassName="h-10 w-full rounded-field border-grey-100 bg-true-white px-3 text-sm font-normal shadow-none dark:border-grey-400 dark:bg-card sm:w-36"
            />
            <div className="flex items-center gap-1">
              <button
                type="button"
                aria-label={t('GRID_VIEW', { defaultValue: 'Grid view' })}
                aria-pressed={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-nav',
                  viewMode === 'grid'
                    ? 'bg-grey-100 dark:bg-true-white/15'
                    : 'bg-transparent hover:bg-grey-50 dark:hover:bg-true-white/10',
                  titleColor
                )}
              >
                <LayoutGrid className="h-5 w-5" strokeWidth={1.75} />
              </button>
              <button
                type="button"
                aria-label={t('LIST_VIEW', { defaultValue: 'List view' })}
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-nav',
                  viewMode === 'list'
                    ? 'bg-grey-100 dark:bg-true-white/15'
                    : 'bg-transparent hover:bg-grey-50 dark:hover:bg-true-white/10',
                  titleColor
                )}
              >
                <AlignJustify className="h-5 w-5" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </div>
        {renderDashboards()}
      </div>
    </div>
  );
}
